<template>
  <h2
    class="mt-0 tw-text-2xl tw-font-semibold tw-mb-2"
    style="color: var(--text-color)"
  >
    Podcasters Management
  </h2>
  <div>
    <!-- Action Buttons To Create New Podcaster/Network -->
    <div
      class="flex justify-content-between align-items-center gap-2 mb-2 p-2 flex-wrap"
    >
      <div v-if="activeTab === 'all'" class="flex gap-2">
        <Button class="p-button-sm" @click="goToCreateNewPodcaster()"
          >Add New Podcaster</Button
        >
        <Button class="p-button-sm" @click="showUploadCSVModal(true)"
          >Add Network</Button
        >
      </div>
      <div v-else></div>

      <!-- Switcher between podcasters and pending podcasters-->
      <div class="flex gap-2">
        <Button
          :class="[
            'p-button-sm',
            activeTab === 'all' ? 'p-button-primary' : 'p-button-outlined',
          ]"
          @click="switchTab('all')"
        >
          All Podcasters
        </Button>
        <Button
          :class="[
            'p-button-sm',
            activeTab === 'pending' ? 'p-button-primary' : 'p-button-outlined',
          ]"
          @click="switchTab('pending')"
        >
          Pending Podcasters
        </Button>
      </div>
    </div>
    <Card>
      <template #header>
        <div
          class="flex flex-wrap justify-content-between align-items-center p-2 px-4 gap-2"
        >
          <div class="flex gap-2 align-items-center">
            <div v-if="activeTab === 'all'" class="flex gap-2">
              <Button
                severity="warning"
                :label="`All (${allPodcastersInTab.length})`"
                :outlined="currentPlanFilter !== 'all'"
                @click="filterByPlan('all')"
                size="small"
              />
              <Button
                severity="warning"
                :label="`Trial Plan (${trialPodcastersInTab.length})`"
                :outlined="currentPlanFilter !== 'trial'"
                @click="filterByPlan('trial')"
                size="small"
              />
              <Button
                severity="warning"
                :label="`Regular Plan (${regularPodcastersInTab.length})`"
                :outlined="currentPlanFilter !== 'regular'"
                @click="filterByPlan('regular')"
                size="small"
              />
              <div
                v-if="currentPlanFilter === 'trial'"
                class="flex align-items-center gap-2"
              >
                <Checkbox
                  v-model="activeTrialOnly"
                  :binary="true"
                  inputId="active-only-checkbox"
                />
                <label for="active-only-checkbox" class="cursor-pointer"
                  >Active campaigns only</label
                >
              </div>
            </div>
            <div v-else class="flex gap-2">
              <Button
                severity="warning"
                label="Verified"
                :outlined="pendingFilter !== 'verified'"
                @click="pendingFilter = 'verified'"
                size="small"
              />
              <Button
                severity="warning"
                label="Not Verified"
                :outlined="pendingFilter !== 'not-verified'"
                @click="pendingFilter = 'not-verified'"
                size="small"
              />
            </div>
          </div>
          <InputText
            class="w-full sm:w-auto"
            type="text"
            v-model="podcastersTableFilter.global.value"
            placeholder="Global Search"
          />
        </div>
      </template>
      <template #content>
        <div
          v-if="networkFilterId"
          class="tw-flex tw-justify-between tw-items-center tw-rounded tw-p-2 tw-mb-2 tw-bg-surface-0"
        >
          <span>
            Showing podcasters for network
            <span class="tw-font-semibold">
              {{ networkFilterName || networkFilterId }}
            </span>
          </span>
          <Button
            label="Clear"
            class="p-button-sm p-button-outlined"
            severity="secondary"
            @click="clearNetworkFilter"
          />
        </div>
        <DataTable
          :value="displayedPodcasters"
          :paginator="true"
          :rows="10"
          :first="initFirst"
          @page="onChangePage"
          :filters="podcastersTableFilter"
          :row-class="
            (data) =>
              isFinishedTrial(data)
                ? 'tw-bg-red-50 dark:tw-bg-red-900/60'
                : null
          "
          class="p-datatable-sm"
          :stripedRows="true"
          :globalFilterFields="
            activeTab === 'all'
              ? [
                  'id',
                  'name',
                  'email',
                  'podcaster_type',
                  'plan',
                  'referrer_name',
                  'network_name',
                  'campaigns_end_date',
                ]
              : [
                  'id',
                  'name',
                  'email',
                  'referrer_email',
                  'referrer_name',
                  'network_name',
                  'network_event_name',
                ]
          "
        >
          <Column field="id" header="ID" :sortable="true" style="width: 5%">
            <template #body="slotProps">
              <div class="truncate-text" :title="slotProps.data.id">
                {{ slotProps.data.id }}
              </div>
            </template>
          </Column>
          <Column
            field="name"
            header="Name"
            :sortable="true"
            style="width: 15%"
          >
            <template #body="slotProps">
              <div class="truncate-text" :title="slotProps.data.name">
                {{ slotProps.data.name }}
              </div>
            </template>
          </Column>
          <Column
            field="email"
            header="Email"
            :sortable="true"
            :style="{ width: '15%' }"
          >
            <template #body="slotProps">
              <div class="truncate-text" :title="slotProps.data.email">
                {{ slotProps.data.email }}
              </div>
            </template>
          </Column>
          <Column
            field="created_at"
            header="Created At"
            :sortable="true"
            :style="{ width: '15%' }"
          >
            <template #body="slotProps">
              <div class="date-cell" :title="slotProps.data.created_at">
                {{
                  slotProps.data.created_at || slotProps.data.last_fetched || ""
                }}
              </div>
            </template>
          </Column>
          <Column
            v-if="activeTab === 'all'"
            field="podcaster_type"
            header="Type"
            :sortable="true"
            style="width: 8%"
          ></Column>
          <Column
            v-if="activeTab === 'all'"
            field="plan"
            header="Plan"
            :sortable="true"
            style="width: 15%"
          ></Column>
          <Column
            v-if="activeTab === 'all'"
            field="network_name"
            header="Network"
            :sortable="true"
            style="width: 10%"
          >
            <template #body="slotProps">
              <div class="truncate-text" :title="slotProps.data.network_name">
                {{ slotProps.data.network_name || "-" }}
              </div>
            </template>
          </Column>
          <Column
            field="referrer_name"
            header="Referrer"
            :sortable="true"
            style="width: 8%"
            v-if="activeTab === 'all'"
          >
            <template #body="slotProps">
              <div class="truncate-text" :title="slotProps.data.referrer_name">
                {{ slotProps.data.referrer_name || "-" }}
              </div>
            </template>
          </Column>

          <Column
            v-if="currentPlanFilter === 'trial' && activeTab === 'all'"
            field="campaigns_end_date"
            header="Campaigns End Date"
            :sortable="true"
            style="width: 12%"
          >
            <template #body="slotProps">
              <div class="date-cell" :title="slotProps.data.campaigns_end_date">
                {{
                  slotProps.data.campaigns_end_date
                    ? slotProps.data.campaigns_end_date
                    : "-"
                }}
              </div>
            </template>
          </Column>
          <Column
            v-if="activeTab === 'pending'"
            field="status"
            header="Status"
            :sortable="false"
            style="width: 10%"
          >
            <template #body="slotProps">
              <span
                :style="
                  slotProps.data.status === 'approved'
                    ? 'color:#55ffa5;font-weight:bold'
                    : slotProps.data.status === 'rejected' ||
                      slotProps.data.status === 'not-verified'
                    ? 'color:#ffa7fb;font-weight:bold'
                    : ''
                "
                :class="
                  slotProps.data.status === 'pending'
                    ? 'tw-text-amber-300 font-bold'
                    : ''
                "
              >
                {{ slotProps.data.status }}
              </span>
            </template>
          </Column>

          <Column
            v-if="activeTab === 'pending'"
            field="referrer_name"
            header="Referrer Name"
            :sortable="false"
            style="width: 15%"
          >
            <template #body="slotProps">
              <div class="truncate-text" :title="slotProps.data.referrer_name">
                {{ slotProps.data.referrer_name || "-" }}
              </div>
            </template>
          </Column>
          <Column
            v-if="activeTab === 'pending'"
            field="network_name"
            header="Network Name"
            style="width: 15%"
            :sortable="false"
          >
            <template #body="slotProps">
              <div class="truncate-text" :title="slotProps.data.network_name">
                {{ slotProps.data.network_name || "-" }}
              </div>
            </template>
          </Column>
          <Column
            v-if="activeTab === 'pending'"
            field="network_event_name"
            header="Event"
            style="width: 15%"
            :sortable="false"
          >
            <template #body="slotProps">
              <div
                class="truncate-text"
                :title="slotProps.data.network_event_name"
              >
                {{ slotProps.data.network_event_name || "-" }}
              </div>
            </template>
          </Column>
          <Column header="Actions" style="width: 16%">
            <template #body="slotProps">
              <div class="-tw-m-2 tw-p-2 tw-bg-[var(--surface-card)]">
                <div class="flex gap-1">
                  <Button
                    icon="pi pi-rss"
                    class="p-button-sm p-button-outlined"
                    @click="
                      showRSSFeeds(
                        activeTab === 'all'
                          ? slotProps.data.podcaster_id
                          : slotProps.data.id
                      )
                    "
                    title="View RSS Feeds"
                    :disabled="
                      activeTab === 'pending' && !slotProps.data.rss_url
                    "
                  >
                    <small class="text-[10px] font-normal align-middle"
                      >RSS</small
                    >
                  </Button>

                  <template v-if="activeTab === 'pending'">
                    <Button
                      v-if="slotProps.data.status == 'approved'"
                      :disabled="!slotProps.data.podcaster_id"
                      icon="pi pi-search"
                      class="p-button-sm p-button-outlined"
                      title="View Podcaster"
                      @click="viewPodcaster(slotProps.data.email)"
                    />
                  </template>

                  <template v-if="activeTab === 'all'">
                    <Button
                      icon="pi pi-list"
                      class="p-button-sm p-button-outlined"
                      @click="goToShows(slotProps.data.podcaster_id)"
                      title="Shows"
                    />
                    <Button
                      icon="pi pi-megaphone"
                      class="p-button-sm p-button-outlined"
                      @click="goToCampaigns(slotProps.data.podcaster_id)"
                      title="Campaigns"
                    />
                    <Button
                      icon="pi pi-pencil"
                      class="p-button-sm p-button-outlined"
                      @click="goToUpdatePodcaster(slotProps.data.podcaster_id)"
                      title="Edit"
                    />
                    <Button
                      icon="pi pi-trash"
                      class="p-button-sm p-button-outlined p-button-danger"
                      @click="
                        confirmDeletePodcaster(slotProps.data.podcaster_id)
                      "
                      title="Delete"
                    />
                    <Button
                      icon="pi pi-download"
                      class="p-button-sm p-button-outlined"
                      @click="downloadReport(slotProps.data.podcaster_id)"
                      :loading="downloadingReports[slotProps.data.podcaster_id]"
                      :disabled="
                        downloadingReports[slotProps.data.podcaster_id]
                      "
                      title="Download Report"
                    />
                    <Button
                      icon="pi pi-eye"
                      class="p-button-sm p-button-outlined"
                      @click="
                        goToPodcasterOverview(slotProps.data.podcaster_id)
                      "
                      title="Podcaster Overview"
                    />
                    <Button
                      icon="pi pi-envelope"
                      class="p-button-sm p-button-outlined p-button-warning"
                      :loading="
                        sendingPreJamxEmail[slotProps.data.podcaster_id] ===
                        true
                      "
                      :disabled="
                        Number(slotProps.data.missing_avg_downloads) <= 0
                      "
                      @click="sendPreJamxEmail(slotProps.data)"
                      :title="
                        Number(slotProps.data.missing_avg_downloads) > 0
                          ? 'Send missing averages reminder'
                          : 'All shows have averages'
                      "
                    />
                    <Button
                      icon="pi pi-link"
                      class="p-button-sm p-button-outlined"
                      title="Get Affiliate Link"
                      @click="getAffiliateLink(slotProps.data.podcaster_id)"
                    />
                    <Button
                      icon="pi pi-chart-bar"
                      class="p-button-sm p-button-outlined"
                      title="Podcaster Dashboard"
                      @click="goToPodcasterDashboard(slotProps.data)"
                    />
                  </template>
                  <template v-else>
                    <Button
                      v-if="slotProps.data.status === 'pending'"
                      icon="pi pi-user-plus"
                      class="p-button-sm p-button-success"
                      title="Approve"
                      @click="openApproveModal(slotProps.data)"
                      :disabled="approveRejectLoading"
                    />
                    <Button
                      v-if="slotProps.data.status === 'pending'"
                      icon="pi pi-user-minus"
                      class="p-button-sm p-button-danger"
                      title="Reject"
                      @click="openRejectConfirm(slotProps.data)"
                      :disabled="approveRejectLoading"
                    />
                  </template>
                </div>
              </div>
            </template>
          </Column>
          <template #paginatorstart>
            <Button
              type="button"
              icon="pi pi-refresh"
              @click="fetchPodcasters()"
              text
            />
          </template>
          <template #paginatorend>
            <div class="flex gap-2 tw-items-center">
              <Button
                v-if="activeTab === 'all'"
                type="button"
                icon="pi pi-credit-card"
                text
                @click="openBankingDownloadModal = true"
              />

              <download-csv
                :data="csvData.data"
                :name="csvData.name"
                class="download-csv"
              >
                <Button type="button" icon="pi pi-download" text />
              </download-csv>
            </div>
          </template>
        </DataTable>
      </template>
    </Card>
    <Dialog v-model:visible="openRssFeeds" header="RSS Feeds" modal>
      <div style="min-width: 200px">
        <div v-if="!rssFeedsForModal.length" class="text-500 my-2">
          No RSS feeds submitted yet.
        </div>
        <div
          v-for="(feed, index) in rssFeedsForModal"
          :key="index"
          class="flex align-items-center gap-2 my-2"
        >
          <a :href="feed.rss_url" target="_blank" class="flex-1">
            {{ feed.rss_url }}
          </a>
          <span v-if="feed.author">({{ feed.author }})</span>
          <Button
            icon="pi pi-copy"
            class="p-button-sm p-button-text"
            @click="copyToClipboard(feed.rss_url)"
            title="Copy RSS URL"
          />
        </div>
      </div>
    </Dialog>
    <Dialog
      v-model:visible="openUploadCSVModal"
      header="Upload CSV"
      modal
      :style="{ width: '20rem' }"
    >
      <p>Upload csv file to add network</p>
      <FileUpload
        class="w-full mt-4"
        ref="fileUpload"
        mode="basic"
        accept="text/csv"
        :maxFileSize="1000000"
        @select="onSelect"
        customUpload
      />
      <p v-if="showMessage.isShow" class="text-red-400">
        {{ showMessage.message }}
      </p>
      <download-csv :data="sampleCSVDownload" class="mt-2">
        (<span
          class="text-blue-500 hover:text-blue-300 underline cursor-pointer"
          >Download sample csv file</span
        >)
      </download-csv>
      <div class="mt-6 flex justify-content-between">
        <Button
          class="w-full sm:w-auto justify-content-center"
          severity="danger"
          @click="showUploadCSVModal(false)"
          >Cancel
        </Button>
        <Button
          class="w-full sm:w-auto justify-content-center"
          @click="submitCSV()"
          >Submit
        </Button>
      </div>
    </Dialog>
    <Dialog
      v-model:visible="approveModalVisible"
      header="Approve Podcaster"
      modal
      :style="{ width: '800px', maxWidth: '90vw' }"
    >
      <div class="tw-min-w-0">
        <div class="grid">
          <div class="col-12 md:col-6 flex flex-column gap-1">
            <label for="rss-input">RSS Feed Link</label>
            <InputText
              id="rss-input"
              v-model="approveModalRss"
              class="w-full"
              placeholder="RSS feed link"
              :class="{ 'p-invalid': approveModalError && !approveModalRss }"
            />
          </div>
          <div class="col-12 md:col-6 flex flex-column gap-1">
            <label for="avg-downloads">Avg Monthly Downloads</label>
            <InputNumber
              id="avg-downloads"
              v-model="approveModalAvgDownloads"
              :useGrouping="true"
              :max="MAX_AVG_MONTHLY_DOWNLOADS"
              placeholder="Enter avg monthly downloads"
              class="w-full"
              :class="{
                'p-invalid': approveModalError && !approveModalAvgDownloads,
              }"
            />
          </div>
        </div>

        <div class="mt-2">
          <CampaignTypesForm
            v-model:campaigns="approveModalCampaigns"
            :show-error="!!approveModalError"
          />
        </div>

        <span class="text-red-500 text-sm block" :style="{ minHeight: '21px' }">
          <i v-if="approveModalError">{{ approveModalError }}</i>
        </span>

        <div class="flex gap-2 mt-2 justify-end">
          <Button
            label="Cancel"
            @click="handleApproveCancel"
            class="p-button-text"
          />
          <Button
            label="Approve"
            @click="handleApprove"
            class="p-button-success"
            :loading="approveRejectLoading"
          />
        </div>
      </div>
    </Dialog>
    <Dialog
      v-model:visible="rejectConfirmVisible"
      header="Reject Podcaster"
      modal
    >
      <div>
        <p>Are you sure you want to reject this podcaster?</p>
        <div class="flex gap-2 mt-4 justify-end">
          <Button
            label="Cancel"
            @click="handleRejectCancel"
            class="p-button-text"
          />
          <Button
            label="Reject"
            @click="handleReject"
            class="p-button-danger"
          />
        </div>
      </div>
    </Dialog>
    <Dialog
      v-model:visible="openBankingDownloadModal"
      header="Download banking information"
      modal
    >
      <div class="flex flex-column gap-4" style="min-width: 400px">
        <div>
          <label for="banking-number" class="block mb-2"
            >Get Newest Podcasters</label
          >
          <InputText
            id="banking-number"
            type="number"
            v-model.number="bankingDownloadNumber"
            :disabled="bankingDownloadAll || bankingDownloadLoading"
            class="w-full"
            min="1"
          />
        </div>
        <div class="flex gap-3 tw-items-end">
          <label for="banking-download-all">Download all</label>
          <Checkbox
            id="banking-download-all"
            v-model="bankingDownloadAll"
            :binary="true"
            inputId="banking-download-all"
            :disabled="bankingDownloadLoading"
          />
        </div>
      </div>
      <div class="flex gap-2 justify-end mt-4">
        <Button
          label="Cancel"
          @click="openBankingDownloadModal = false"
          class="p-button-text"
          :disabled="bankingDownloadLoading"
        />
        <Button
          label="Download"
          @click="handleBankingDownload"
          class="p-button-success"
          :loading="bankingDownloadLoading"
          :disabled="bankingDownloadLoading"
        />
      </div>
    </Dialog>
  </div>
</template>

<script setup>
import Card from "primevue/card";
import Checkbox from "primevue/checkbox";
import Button from "primevue/button";
import CampaignTypesForm from "./CampaignTypesForm.vue";
import FileUpload from "primevue/fileupload";
import { ref, computed, onBeforeMount, watch } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { FilterMatchMode } from "primevue/api";
import axios from "axios";
import { API_BASE_URL, MAX_AVG_MONTHLY_DOWNLOADS } from "@/consts";
import Dialog from "primevue/dialog";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Papa from "papaparse";

let tempFirst = 0;
const initFirst = ref(Number(localStorage.getItem("first")));
const openRssFeeds = ref(false);
const openUploadCSVModal = ref(false);
const fileUpload = ref();
const showMessage = ref({
  isShow: false,
  message: "",
});
const store = useStore();
const toast = useToast();
const confirm = useConfirm();
const router = useRouter();
const route = useRoute();
const modalPodcasterId = ref("");
const downloadingReports = ref({});
const token = localStorage.getItem("podcastAdminToken");
const podcastersTableFilter = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
});
const networkFilterId = computed(() => {
  const { networkId } = route.query;
  if (!networkId) {
    return null;
  }
  return Array.isArray(networkId) ? networkId[0] : networkId;
});
const networkFilterName = computed(() => {
  const { networkName } = route.query;
  if (!networkName) {
    return null;
  }
  return Array.isArray(networkName) ? networkName[0] : networkName;
});
const sampleCSVDownload = ref([
  {
    name: "John Doe",
    email: "emailaddress@example.com",
    avg_per_month: 10000,
    rss: "https://samplesite.com/rss-feed-url-1",
  },
  {
    name: "",
    email: "",
    avg_per_month: 11000,
    rss: "https://samplesite.com/rss-feed-url-2",
  },
  {
    name: "",
    email: "",
    avg_per_month: 12000,
    rss: "https://samplesite.com/rss-feed-url-3",
  },
]);

const switchTab = (tab) => {
  activeTab.value = tab;
  if (tab === "pending") {
    fetchPendingPodcasters();
  } else {
    fetchPodcasters();
  }
};

const activeTab = ref("all");
const currentPlanFilter = ref("all");
const pendingFilter = ref("verified");
const pendingPodcasters = ref([]);
const approveModalVisible = ref(false);
const approveModalRss = ref("");
const approveModalAvgDownloads = ref(null);
const approveModalCampaigns = ref({
  promotion: {
    enabled: false,
    interval: null,
  },
  spotify: {
    enabled: false,
    interval: null,
  },
});
const approveModalError = ref("");
const approveModalPodcaster = ref(null);
const approveRejectLoading = ref(false);
const rejectConfirmVisible = ref(false);
const rejectModalPodcaster = ref(null);
const bankingDownloadAll = ref(false);
const bankingDownloadNumber = ref(10);
const openBankingDownloadModal = ref(false);
const bankingDownloadLoading = ref(false);
const sendingPreJamxEmail = ref({});
const activeTrialOnly = ref(false);

watch(approveModalAvgDownloads, (value) => {
  if (value > MAX_AVG_MONTHLY_DOWNLOADS) {
    approveModalAvgDownloads.value = MAX_AVG_MONTHLY_DOWNLOADS;
  }
});

const fetchPendingPodcasters = async () => {
  try {
    const response = await axios.get(
      API_BASE_URL + "/podcaster-join/pending-podcasters",
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    pendingPodcasters.value = Array.isArray(response.data.data)
      ? response.data.data.sort((a, b) => {
          return b.id - a.id;
        })
      : [];
  } catch (error) {
    console.error("Error fetching pending podcasters:", error);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to fetch pending podcasters",
      life: 4000,
    });
  }
};

const filteredPendingPodcasters = computed(() => {
  if (pendingFilter.value === "not-verified")
    return pendingPodcasters.value.filter((p) => p.status === "not-verified");
  return pendingPodcasters.value.filter((p) => p.status !== "not-verified");
});

const displayedPodcasters = computed(() => {
  if (activeTab.value === "all") return filteredPodcasters.value;
  return filteredPendingPodcasters.value;
});

const clearNetworkFilter = () => {
  const newQuery = { ...route.query };
  delete newQuery.networkId;
  delete newQuery.networkName;
  router.replace({
    name: "PodcastersList",
    query: newQuery,
  });
};

const goToCreateNewPodcaster = () => {
  router.push({ name: "AddPodcaster" });
};

const goToUpdatePodcaster = (podcasterId) => {
  router.push({
    name: "UpdatePodcaster",
    params: { podcasterId },
  });
};

const goToPodcasterDashboard = (podcasterData) => {
  router.push({
    name: "PodcastAdminPodcasterDashboard",
    query: {
      podcasterId: podcasterData.podcaster_id,
      connectedPublisherId: podcasterData.connected_publisher_id,
      plan: podcasterData.plan,
    },
  });
};

const goToPodcasterOverview = (podcasterId) => {
  router.push({ name: "PodcasterOverview", params: { podcasterId } });
};

const getAffiliateLink = (podcasterId) => {
  const link = "https://jamx.ai/podcasters-offer?ref_id=" + podcasterId;
  copyToClipboard(link, "Affiliate link copied to clipboard");
};

const sendPreJamxEmail = async (podcaster) => {
  const podcasterId = podcaster?.podcaster_id;
  if (!podcasterId) return;

  sendingPreJamxEmail.value = {
    ...sendingPreJamxEmail.value,
    [podcasterId]: true,
  };

  try {
    await axios.post(
      `${API_BASE_URL}/podcast-admin/podcasters/${podcasterId}/prejamx-email`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    toast.add({
      severity: "success",
      summary: "Email sent",
      detail: `Reminder sent to ${podcaster.email}`,
      life: 4000,
    });
  } catch (error) {
    console.error("Error sending pre-jamx email:", error);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: error?.response?.data?.message || "Failed to send reminder email",
      life: 4000,
    });
  } finally {
    sendingPreJamxEmail.value = {
      ...sendingPreJamxEmail.value,
      [podcasterId]: false,
    };
  }
};

const copyToClipboard = (text, successMessage = "Link copied to clipboard") => {
  navigator.clipboard.writeText(text).then(
    function () {
      toast.add({
        severity: "success",
        summary: "Success",
        detail: successMessage,
        life: 4000,
      });
    },
    function () {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to copy to clipboard",
        life: 4000,
      });
    }
  );
};

const goToShows = (podcasterId) => {
  router.push({
    name: "PodcastAdminShowsList",
    query: { podcasterId },
  });
};

const goToCampaigns = (podcasterId) => {
  localStorage.setItem("first", tempFirst);
  router.push({
    name: "PodcastAdminCampaignsList",
    query: { podcasterId },
  });
};

const onChangePage = (e) => {
  tempFirst = e.first;
  localStorage.removeItem("first");
};

const showUploadCSVModal = (flag) => {
  openUploadCSVModal.value = flag;
  showMessage.value.isShow = false;
};

const onSelect = () => {
  if (
    fileUpload.value.files.length > 0 ||
    fileUpload.value.files[0].type == "text/csv"
  )
    showMessage.value.isShow = false;
  else {
    showMessage.value.isShow = true;
    showMessage.value.message = "Please select CSV file";
  }
};

const submitCSV = async () => {
  if (
    fileUpload.value.files.length == 0 ||
    fileUpload.value.files[0].type != "text/csv"
  ) {
    showMessage.value.isShow = true;
    showMessage.value.message = "Please select CSV file";
    return;
  }
  try {
    let results = await new Promise((resolve, reject) => {
      Papa.parse(fileUpload.value.files[0], {
        complete: resolve,
        error: reject,
        skipEmptyLines: true,
      });
    });

    results = results.data.filter((result) => result.length == 4);
    if (results.length < 2 || results[0].length != 4) {
      showMessage.value.isShow = true;
      showMessage.value.message = "Wrong CSV format!";
      return;
    }
    let name = results[1][0];
    let email = results[1][1];
    let shows = [];
    for (let i = 1; i < results.length; i++) {
      shows.push({
        avg_per_month: results[i][2],
        rss: results[i][3],
      });
    }
    store.commit("setLoading", true);

    try {
      const response = await axios.post(
        API_BASE_URL + "/podcaster/csv-submit",
        {
          name,
          email,
          shows,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.success) {
        toast.add({
          severity: "success",
          summary: "Success",
          detail: `Shows created successfully`,
          life: 4000,
        });
        store.dispatch("loadPodcasters", { token });
        showUploadCSVModal();
      } else
        toast.add({
          severity: "error",
          summary: "Server error...",
          life: 4000,
        });
    } catch (error) {
      console.log(error);
      toast.add({
        severity: "error",
        summary: "Server error...",
        life: 4000,
      });
    }
  } catch (error) {
    console.error("Error parsing CSV:", error);
  }
  store.commit("setLoading", false);
};

const podcasters = computed(() => store.getters.podcasters);

const allPodcastersInTab = computed(() => {
  const all = podcasters.value || [];
  if (!networkFilterId.value) return all;
  const targetId = networkFilterId.value.toString();
  return all.filter((item) => (item.network_id?.toString() ?? "") === targetId);
});

const trialPodcastersInTab = computed(() =>
  allPodcastersInTab.value.filter((p) => p.plan === "trial")
);

const regularPodcastersInTab = computed(() =>
  allPodcastersInTab.value.filter((p) => p.plan !== "trial")
);

const filteredPodcasters = computed(() => {
  let base = allPodcastersInTab.value;

  if (currentPlanFilter.value === "trial")
    base = base.filter((p) => p.plan === "trial");
  else if (currentPlanFilter.value === "regular")
    base = base.filter((p) => p.plan !== "trial");

  if (currentPlanFilter.value === "trial" && activeTrialOnly.value) {
    base = base.filter((p) => {
      if (!p.campaigns_end_date) return false;
      return new Date(p.campaigns_end_date) > new Date();
    });
  }

  return base.sort((a, b) => {
    const dateA = new Date(a.created_at || 0);
    const dateB = new Date(b.created_at || 0);
    return dateB - dateA;
  });
});

const isFinishedTrial = computed(
  () => (data) =>
    activeTab.value === "all" &&
    currentPlanFilter.value === "trial" &&
    (!data.campaigns_end_date || new Date(data.campaigns_end_date) < new Date())
);

const filterByPlan = (plan) => {
  currentPlanFilter.value = plan;
};

const csvData = computed(() => {
  const isPending = activeTab.value === "pending";
  return {
    name: isPending
      ? `podcasters-pending-${pendingFilter.value}.csv`
      : `podcasters-${currentPlanFilter.value}.csv`,
    data: isPending
      ? filteredPendingPodcasters.value
      : filteredPodcasters.value.map((item) => ({
          ...item,
          rss_urls: item.rss_urls
            ? item.rss_urls.map((r) => `${r.rss_url}(${r.author})`).join(",")
            : [],
        })),
  };
});

const fetchPodcasters = async () => {
  store.dispatch("loadPodcasters", {
    token,
    fields: [
      "podcaster_id",
      "id",
      "name",
      "email",
      "created_at",
      "podcaster_type",
      "plan",
      "network_name",
      "network_id",
      "referrer_name",
      "campaigns_end_date",
      "rss_urls",
      "missing_avg_downloads",
      "connected_publisher_id",
    ],
  });
};

const handleDeletePodcaster = async (podcaster_id) => {
  try {
    const response = await axios.delete(
      API_BASE_URL + "/podcaster/" + podcaster_id,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.success) {
      toast.add({
        severity: "success",
        summary: "Success",
        detail: "Podcaster has been deleted",
        life: 4000,
      });
      store.dispatch("loadPodcasters", { token });
    } else {
      toast.add({
        severity: "error",
        summary: "Podcaster not found",
        life: 4000,
      });
    }
  } catch (error) {
    console.log(error);
    toast.add({
      severity: "error",
      summary: "Server error...",
      life: 4000,
    });
  }
};

const downloadReport = async (podcaster_id, format = "csv") => {
  let response;
  downloadingReports.value[podcaster_id] = true;

  try {
    response = await axios.get(
      `${API_BASE_URL}/admin/reports/performance/${podcaster_id}?format=${format}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      }
    );

    const file = new Blob([response.data], {
      type: format === "pdf" ? "application/pdf" : "text/csv",
    });

    const fileURL = URL.createObjectURL(file);
    const link = document.createElement("a");
    link.href = fileURL;
    link.download = `report_${podcaster_id}.${format}`;
    link.click();

    URL.revokeObjectURL(fileURL);
  } catch (error) {
    console.error("Error downloading report: ", error);
    toast.add({
      severity: "error",
      summary: "Error downloading report",
      life: 4000,
    });
  } finally {
    downloadingReports.value[podcaster_id] = false;
  }
};

const confirmDeletePodcaster = (podcaster_id) => {
  confirm.require({
    message: "Do you want to delete this podcaster?",
    header: "Delete Confirmation",
    icon: "pi pi-info-circle",
    acceptClass: "p-button-danger",
    accept: () => {
      handleDeletePodcaster(podcaster_id);
    },
  });
};

const showRSSFeeds = (podcasterId) => {
  modalPodcasterId.value = podcasterId;
  openRssFeeds.value = true;
};

const rssFeedsForModal = computed(() => {
  if (activeTab.value === "all") {
    const podcaster = podcasters.value.find(
      ({ podcaster_id }) => podcaster_id == modalPodcasterId.value
    );
    return podcaster?.rss_urls || [];
  } else {
    const podcaster = pendingPodcasters.value.find(
      ({ id }) => id == modalPodcasterId.value
    );

    if (!podcaster || !podcaster.rss_url) {
      return [];
    }

    return [
      {
        rss_url: podcaster.rss_url,
        author: podcaster.author || "",
      },
    ];
  }
});

const handleBankingDownload = async () => {
  bankingDownloadLoading.value = true;
  const limit = bankingDownloadAll.value ? null : bankingDownloadNumber.value;

  try {
    const response = await axios.get(
      `${API_BASE_URL}/podcasters/banking-details/${limit || "all"}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.success && response.data?.data.length > 0) {
      const bankingCsvData = response.data.data;

      const csvContent = [
        Object.keys(bankingCsvData[0]),
        ...bankingCsvData.map((row) => Object.values(row)),
      ]
        .map((row) => row.map((field) => `"${field || ""}"`).join(","))
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `banking-details-${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      openBankingDownloadModal.value = false;

      toast.add({
        severity: "success",
        summary: "Success",
        detail: "Banking details downloaded successfully",
        life: 4000,
      });
    } else {
      toast.add({
        severity: "warn",
        summary: "No Data",
        detail: "No banking details found",
        life: 4000,
      });
    }
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to download banking details",
      life: 4000,
    });
  } finally {
    bankingDownloadLoading.value = false;
  }
};

const openApproveModal = (podcaster) => {
  approveModalPodcaster.value = podcaster;
  approveModalRss.value = podcaster.rss_url || "";
  approveModalAvgDownloads.value = podcaster.avg_monthly_downloads
    ? Number(podcaster.avg_monthly_downloads)
    : null;
  approveModalCampaigns.value = {
    promotion: {
      enabled: false,
      interval: null,
    },
    spotify: {
      enabled: false,
      interval: null,
    },
  };
  approveModalError.value = "";
  approveModalVisible.value = true;
};

const validateApprovalForm = () => {
  if (!approveModalRss.value.trim()) {
    return "RSS feed link is required";
  }

  try {
    const url = new URL(approveModalRss.value.toLowerCase().trim());
    if (!/^https?:/.test(url.protocol)) {
      throw new Error();
    }
  } catch {
    return "Please enter a valid RSS feed link (URL)";
  }

  const normalizedAvgDownloads =
    approveModalAvgDownloads.value == null ||
    approveModalAvgDownloads.value === ""
      ? null
      : Number(approveModalAvgDownloads.value);

  if (!Number.isFinite(normalizedAvgDownloads) || normalizedAvgDownloads <= 0) {
    return "Avg monthly downloads is required and must be greater than 0";
  }

  const hasSelectedCampaign =
    approveModalCampaigns.value.promotion.enabled ||
    approveModalCampaigns.value.spotify.enabled;
  if (!hasSelectedCampaign) {
    return "At least one campaign type must be selected";
  }

  if (
    approveModalCampaigns.value.promotion.enabled &&
    !approveModalCampaigns.value.promotion.interval
  ) {
    return "Promotion interval must be greater than 0";
  }

  if (
    approveModalCampaigns.value.spotify.enabled &&
    !approveModalCampaigns.value.spotify.interval
  ) {
    return "Spotify interval must be greater than 0";
  }

  return null;
};

const handleApprove = async () => {
  const validationError = validateApprovalForm();
  if (validationError) {
    approveModalError.value = validationError;
    return;
  }

  approveModalVisible.value = false;
  approveRejectLoading.value = true;

  const normalizedAvgDownloads =
    approveModalAvgDownloads.value == null ||
    approveModalAvgDownloads.value === ""
      ? null
      : Number(approveModalAvgDownloads.value);

  try {
    const payload = {
      newRssUrl: approveModalRss.value.trim(),
      avgMonthlyDownloads: normalizedAvgDownloads,
      campaigns: [
        ...(approveModalCampaigns.value.promotion.enabled
          ? [
              {
                type: "promotion",
                interval: approveModalCampaigns.value.promotion.interval,
              },
            ]
          : []),
        ...(approveModalCampaigns.value.spotify.enabled
          ? [
              {
                type: "spotify",
                interval: approveModalCampaigns.value.spotify.interval,
              },
            ]
          : []),
      ],
    };

    await axios.post(
      `${API_BASE_URL}/podcaster-join/pending-podcaster/${approveModalPodcaster.value.pending_podcaster_id}/approve`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    toast.add({
      severity: "success",
      summary: "Podcaster approved",
      life: 3000,
    });
    await fetchPendingPodcasters();
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Error approving podcaster",
      detail:
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Server error",
      life: 4000,
    });
  } finally {
    approveRejectLoading.value = false;
  }
};

const handleApproveCancel = () => {
  approveModalVisible.value = false;
  approveModalError.value = "";
};

const openRejectConfirm = (podcaster) => {
  rejectModalPodcaster.value = podcaster;
  rejectConfirmVisible.value = true;
};

const handleReject = async () => {
  rejectConfirmVisible.value = false;
  approveRejectLoading.value = true;
  try {
    await axios.post(
      `${API_BASE_URL}/podcaster-join/pending-podcaster/${rejectModalPodcaster.value.pending_podcaster_id}/reject`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    toast.add({
      severity: "success",
      summary: "Podcaster rejected",
      life: 3000,
    });
    await fetchPendingPodcasters();
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Error rejecting podcaster",
      detail: error.response?.data?.error || "Server error",
      life: 4000,
    });
  } finally {
    approveRejectLoading.value = false;
  }
};

const handleRejectCancel = () => {
  rejectConfirmVisible.value = false;
};

const fetchShows = () => {
  store.dispatch("loadShows", {
    token,
    fields: ["show_id", "show_title", "podcaster_id"],
  });
};

const viewPodcaster = (email) => {
  activeTab.value = "all";
  podcastersTableFilter.value.global.value = email;
};

onBeforeMount(() => {
  if (route.query.search) {
    podcastersTableFilter.value.global.value = route.query.search;
    activeTab.value = "all";
  }
  if (networkFilterId.value) {
    activeTab.value = "all";
  }
  fetchPodcasters();
  fetchShows();
});
</script>

<style scoped>
.p-datatable .p-datatable-thead > tr > th,
.p-datatable .p-datatable-tbody > tr > td {
  padding: 0.5rem;
  font-size: 0.9rem;
}

.p-card .p-card-body {
  padding: 0.5rem;
}

.p-dropdown,
.p-inputtext,
.p-button.p-button-sm {
  font-size: 0.9rem;
}

.p-button.p-button-sm {
  padding: 0.3rem 0.5rem;
}

.truncate-text {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.date-cell {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.p-datatable .p-datatable-tbody > tr > td {
  max-height: 3.5em;
  overflow: hidden;
}
</style>
